import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from './cn';
import { focusRing } from './focus-ring';

/**
 * Toggle chip for result filters (design-spec §3 "Filter chip"): 34px, padding
 * 8×14, 13/500 on surface with a 1px line. Active = accent-tint fill, 1px
 * accent border, link-coloured 600 text with a leading ✓.
 *
 * Toggle chips expose `aria-pressed`. Dropdown chips ("Price ▾") pass
 * `hasMenu` and instead expose `aria-haspopup` / `aria-expanded`.
 */
export interface FilterChipProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'children'> {
  label: ReactNode;
  pressed?: boolean;
  /** Renders a trailing ▾ and menu semantics instead of a toggle. */
  hasMenu?: boolean;
  expanded?: boolean;
  /** Optional leading icon shown when not pressed. */
  icon?: ReactNode;
}

export const FilterChip = forwardRef<HTMLButtonElement, FilterChipProps>(
  function FilterChip(
    {
      label,
      pressed = false,
      hasMenu = false,
      expanded,
      icon,
      className,
      ...rest
    },
    ref
  ) {
    return (
      <button
        ref={ref}
        type="button"
        aria-pressed={hasMenu ? undefined : pressed}
        aria-haspopup={hasMenu ? 'menu' : undefined}
        aria-expanded={hasMenu ? Boolean(expanded) : undefined}
        className={cn(
          'hit-44 inline-flex h-ctl-filter items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 font-sans text-[13px] leading-none transition-colors',
          focusRing,
          pressed
            ? 'border-sl-accent bg-sl-accent-tint font-semibold text-sl-link'
            : 'border-sl-line bg-sl-surface font-medium text-sl-ink hover:border-sl-line-strong',
          className
        )}
        {...rest}
      >
        {pressed ? (
          <Check className="h-3.5 w-3.5" strokeWidth={2.4} aria-hidden="true" />
        ) : (
          icon
        )}
        {label}
        {hasMenu && (
          <ChevronDown className="h-3.5 w-3.5 opacity-70" aria-hidden="true" />
        )}
      </button>
    );
  }
);
